import { Reducer } from 'redux';

import { RolloutHistory } from '../../../lib/PlannerDTOs';
import { ActionType } from '../../../store/actionTypes';
import {
  EntityAction,
  ErrorAction,
  Action as PlainAction,
} from '../../../store/types';
import { EntityResponsePayload } from '../../../store/types/playloadTypes';

export interface RolloutState {
  latestRollouts: {
    [variableName: string]: string;
  };
  view: {
    loadingRolloutVersion: boolean;
    loadingLatestRollout: boolean;
  };
}

export const initialState: RolloutState = {
  latestRollouts: {},
  view: {
    loadingRolloutVersion: false,
    loadingLatestRollout: false,
  },
};

type Action =
  | PlainAction<ActionType.FETCH_LATEST_ROLLOUT_STARTED>
  | EntityAction<ActionType.FETCH_LATEST_ROLLOUT_SUCCEEDED>
  | ErrorAction<ActionType.FETCH_LATEST_ROLLOUT_FAILED>
  | EntityAction<ActionType.FETCH_ROLLOUT_HISTORY_SUCCEEDED>
  | PlainAction<ActionType.FETCH_ROLLOUT_VERSION_STARTED>
  | EntityAction<ActionType.FETCH_ROLLOUT_VERSION_SUCCEEDED>
  | ErrorAction<ActionType.FETCH_ROLLOUT_VERSION_FAILED>;

const rolloutReducer: Reducer<RolloutState, Action> = (
  state: RolloutState = initialState,
  action: Action,
) => {
  switch (action.type) {
    case ActionType.FETCH_LATEST_ROLLOUT_STARTED:
      return {
        ...state,
        view: {
          ...state.view,
          loadingLatestRollout: true,
        },
      };

    case ActionType.FETCH_LATEST_ROLLOUT_SUCCEEDED: {
      const [name, version] = decodeRolloutKey(action.payload.result as string);
      return {
        ...state,
        latestRollouts: {
          ...state.latestRollouts,
          [name]: version,
        },
        view: {
          ...state.view,
          loadingLatestRollout: false,
        },
      };
    }
    case ActionType.FETCH_LATEST_ROLLOUT_FAILED:
      return {
        ...state,
        view: {
          ...state.view,
          loadingLatestRollout: false,
        },
      };

    case ActionType.FETCH_ROLLOUT_HISTORY_SUCCEEDED: {
      const [variableName, latestVersion] = extractLatestVersion(
        action.payload,
      );
      if (variableName && latestVersion) {
        return {
          ...state,
          latestRollouts: {
            ...state.latestRollouts,
            [variableName]: latestVersion,
          },
        };
      }
      return state;
    }

    case ActionType.FETCH_ROLLOUT_VERSION_STARTED:
      return {
        ...state,
        view: {
          ...state.view,
          loadingRolloutVersion: true,
        },
      };

    case ActionType.FETCH_ROLLOUT_VERSION_SUCCEEDED:
    case ActionType.FETCH_ROLLOUT_VERSION_FAILED:
      return {
        ...state,
        view: {
          ...state.view,
          loadingRolloutVersion: false,
        },
      };

    default:
      return state;
  }
};

function decodeRolloutKey(key: string): [string, string] {
  const splits = key.split('--');
  return [splits[0], splits[1]];
}

function extractLatestVersion(
  payload: EntityResponsePayload,
): [string, string] {
  const entities = payload.entities.rolloutHistory;
  const variableName = payload.result as string;
  const historyEntity: RolloutHistory | null =
    entities && variableName
      ? (entities[variableName] as RolloutHistory)
      : null;

  // Old rollout might not have versions
  let latestVersion = '';
  if (historyEntity!.versions && historyEntity!.versions.length > 0) {
    const latestVersionEntity = historyEntity!.versions.reduce(function (a, b) {
      return a.version.localeCompare(b.version) > 0 ? a : b;
    });
    latestVersion = latestVersionEntity.version;
  }
  return [variableName, latestVersion];
}

export default rolloutReducer;
